import { User } from '@/shared/api/models'

/* auth state when user signs in / out */
export const STORAGE_AUTH_EVENT_KEY = 'storage_auth_change'
export const STORAGE_AUTH_STATE_KEY = 'auth_module_auth_state'
export const STORAGE_PROFILE_KEY = 'auth_module_profile'

const REFRESH_TOKEN_KEY = 'auth_module_refresh_token'
const ACCESS_TOKEN_KEY = 'auth_module_access_token'

export type AuthState = 'signedIn' | 'signedOut'
export type AuthStorageData = {
  profile: User
  lastUpdated: number
}

export type AuthEvent = CustomEvent<{ authState: AuthState }>

export class AuthStorageHandler {
  storage: Storage

  constructor(storage: Storage) {
    this.storage = storage

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.handleStorageChange)
    }
  }

  private handleStorageChange = (event: StorageEvent) => {
    const { oldValue, newValue, type, key } = event

    if (oldValue === newValue || type !== 'storage' || key !== STORAGE_AUTH_STATE_KEY) {
      return
    }

    this.sendAuthStateEvent(newValue as AuthState)
  }

  cleanup = () => {
    window.removeEventListener('storage', this.handleStorageChange)
  }

  /* auth state */
  sendAuthStateEvent = (authState: AuthState) => {
    const event = new CustomEvent(STORAGE_AUTH_EVENT_KEY, { detail: { authState } })

    window.dispatchEvent(event)
  }

  getAuthState = (): AuthState => {
    return (this.storage?.getItem(STORAGE_AUTH_STATE_KEY) as AuthState | undefined) || 'signedOut'
  }

  setAuthState = (authState: AuthState) => {
    this.storage.setItem(STORAGE_AUTH_STATE_KEY, authState)
    this.sendAuthStateEvent(authState)
  }

  /* access & refresh token */
  getAccessToken = () => this.storage.getItem(ACCESS_TOKEN_KEY) || null

  setAccessToken = (token?: string) => {
    if (!token) return

    this.storage.setItem(ACCESS_TOKEN_KEY, token)
  }

  getRefreshToken = () => this.storage.getItem(REFRESH_TOKEN_KEY) || null

  setRefreshToken = (token?: string) => {
    if (!token) return

    this.storage.setItem(REFRESH_TOKEN_KEY, token)
  }

  /* profile state*/
  getProfile = (): AuthStorageData | null => {
    const data = this.storage.getItem(STORAGE_PROFILE_KEY)

    return data ? JSON.parse(data) : null
  }

  setProfile = (profile: User) => {
    const data: AuthStorageData = {
      profile,
      lastUpdated: Date.now(),
    }

    this.storage.setItem(STORAGE_PROFILE_KEY, JSON.stringify(data))
  }

  clearProfile = () => {
    this.storage.removeItem(STORAGE_PROFILE_KEY)
  }

  /* performed on logout */
  clearAuthStorage = () => {
    this.setAuthState('signedOut')
    this.storage.removeItem(ACCESS_TOKEN_KEY)
    this.storage.removeItem(REFRESH_TOKEN_KEY)
  }
}
